import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Styles/HomePage.css";
import Footer from "./Footer";

const HERO_PHASES = ["DESIGN", "FABRICATE", "INTEGRATE", "COMPETE"];

function useInView(options = {}) {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: options.threshold ?? 0.12, rootMargin: options.rootMargin ?? "0px 0px -8%" }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [options.threshold, options.rootMargin]);

    return [ref, isVisible];
}

function HomePage() {
    const [phaseIndex, setPhaseIndex] = useState(0);
    const navigate = useNavigate();

    const [statsRef, statsVisible] = useInView();
    const [infoRef, infoVisible] = useInView();
    const [cardsRef, cardsVisible] = useInView();

    useEffect(() => {
        if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return undefined;
        }
        const id = window.setInterval(() => {
            setPhaseIndex((i) => (i + 1) % HERO_PHASES.length);
        }, 2000);
        return () => window.clearInterval(id);
    }, []);

    useEffect(() => {
        const script = document.createElement("script");
        script.src = "/particles.js";
        script.async = true;

        script.onload = () => {
            if (window.particlesJS) {
                const particleColor = "#5eead6";
                const lineColor = "rgba(56, 189, 248, 0.22)";

                fetch("/assets/particles.json")
                    .then((res) => res.json())
                    .then((config) => {
                        config.particles.color.value = particleColor;
                        config.particles.line_linked.color = lineColor;
                        config.interactivity = config.interactivity || {};
                        config.interactivity.detect_on = "canvas";
                        config.interactivity.events = {
                            onhover: { enable: true, mode: "repulse" },
                            onclick: { enable: true, mode: "push" },
                            resize: true,
                        };
                        config.interactivity.modes = {
                            ...(config.interactivity.modes || {}),
                            push: { particles_nb: 14 },
                            repulse: { distance: 140, duration: 0.45 },
                        };
                        window.particlesJS("particles-js", config);
                    })
                    .catch(() => {
                        window.particlesJS.load("particles-js", "/assets/particles.json");
                    });
            }
        };

        document.body.appendChild(script);

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, []);

    const stats = [
        { label: "Weight class", value: "3 lb", unit: "combat" },
        { label: "Robot", value: "TONE", unit: "2025–26" },
        { label: "League", value: "NHRL", unit: "competition" },
        { label: "Build", value: "In-house", unit: "subsystems" },
    ];

    return (
        <div className="homepage">
            <div id="particles-js" className="homepage-particles" aria-hidden="true" />
            <div className="homepage-grid" aria-hidden="true" />
            <div className="homepage-vignette" aria-hidden="true" />

            <section className="hero" aria-labelledby="hero-heading-main">
                <div className="hero-content">
                    <p className="hero-eyebrow">
                        <span className="hero-eyebrow-dot" aria-hidden="true" />
                        Dalhousie University · Student engineering team
                    </p>

                    <div className="hero-title-block">
                        <h1 id="hero-heading-main" className="hero-title-main">
                            Dalhousie Combat Robotics
                        </h1>
                        <div className="hero-phase-slot" aria-live="polite" aria-atomic="true">
                            <span className="hero-phase-label">Current mode</span>
                            <span key={phaseIndex} className="hero-phase-word">
                                {HERO_PHASES[phaseIndex]}
                            </span>
                        </div>
                    </div>

                    <p className="hero-lead">
                        Design, build, and compete with combat-ready machines — mechanical precision, electrical
                        systems, and real-time control in one integrated platform.
                    </p>

                    <div className="hero-actions">
                        <button type="button" className="dcrt-btn dcrt-btn--primary" onClick={() => navigate("/GetInvolved")}>
                            Join the team
                        </button>
                        <button type="button" className="dcrt-btn dcrt-btn--ghost" onClick={() => navigate("/blogs")}>
                            Blog
                        </button>
                    </div>
                </div>

                <div
                    ref={statsRef}
                    className={`hero-stats dcrt-panel ${statsVisible ? "is-visible" : ""}`}
                >
                    <div className="hero-stats-header">
                        <span className="hero-stats-tag">Telemetry</span>
                        <span className="hero-stats-live" aria-hidden="true">
                            <span className="hero-stats-live-dot" /> Live
                        </span>
                    </div>
                    <ul className="hero-stats-grid">
                        {stats.map((s) => (
                            <li key={s.label} className="hero-stat">
                                <span className="hero-stat-label">{s.label}</span>
                                <span className="hero-stat-value">{s.value}</span>
                                <span className="hero-stat-unit">{s.unit}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <div className="content-section">
                <section
                    ref={infoRef}
                    className={`info-section dcrt-panel ${infoVisible ? "is-visible" : ""}`}
                >
                    <p className="dcrt-section-label">Mission</p>
                    <h2 className="dcrt-h2 section-heading">What Drives Us</h2>
                    <div className="info-text">
                        <p>
                            At Dalhousie Combat Robotics, we're not just building bots—we're building the future of
                            competitive engineering. Our team is a dynamic collective of students who design, develop,
                            and deploy combat-ready robots built to dominate in the arena.
                        </p>
                        <p>
                            Our first entry, <strong>TONE</strong> (Terror Of the North East), is a fierce three-pound
                            robot engineered to compete in the National Havoc Robotics League during the 2025–2026
                            season. Every component—from the drivetrain to the weapon system—is built in-house by our
                            passionate subsystem teams.
                        </p>
                        <p>
                            Through this hands-on experience, we blend mechanical precision, electrical systems, and
                            real-time software control into one cutting-edge machine. Whether it's designing CAD,
                            debugging motor controllers, or writing control algorithms, we're all in for the fight.
                        </p>
                    </div>
                </section>

                <section className="subsystem-section">
                    <p className="dcrt-section-label subsystem-section-label">Subsystems</p>
                    <h2 className="dcrt-h2 subsystem-section-title">Integrated machine</h2>
                    <p className="subsystem-section-desc">
                        Four engineering teams. One robot. Click through to learn how each subsystem contributes to TONE.
                    </p>

                    <div ref={cardsRef} className={`card-section ${cardsVisible ? "is-visible" : ""}`}>
                        <article className="card dcrt-panel" style={{ "--stagger": 0 }}>
                            <div className="card-corner" aria-hidden="true" />
                            <div className="card-icon card-icon--drivetrain" aria-hidden="true" />
                            <div className="card-content">
                                <h3 className="card-title">DriveTrain</h3>
                                <p className="card-description">
                                    Responsible for traction, speed, and agility, our drivetrain team ensures TONE stays
                                    mobile and out of harm's way—with the dexterity and power of a winning bot.
                                </p>
                                <button type="button" className="learn-more-btn dcrt-btn dcrt-btn--primary" onClick={() => navigate("/drivetrain")}>
                                    Learn more
                                </button>
                            </div>
                        </article>

                        <article className="card dcrt-panel" style={{ "--stagger": 1 }}>
                            <div className="card-corner" aria-hidden="true" />
                            <div className="card-icon card-icon--chassis" aria-hidden="true" />
                            <div className="card-content">
                                <h3 className="card-title">Chassis</h3>
                                <p className="card-description">
                                    The spine of TONE—team chassis unifies all subsystems with precise mounting, durable
                                    armor, and a secure housing for electronics.
                                </p>
                                <button type="button" className="learn-more-btn dcrt-btn dcrt-btn--primary" onClick={() => navigate("/chassis")}>
                                    Learn more
                                </button>
                            </div>
                        </article>

                        <article className="card dcrt-panel" style={{ "--stagger": 2 }}>
                            <div className="card-corner" aria-hidden="true" />
                            <div className="card-icon card-icon--weapon" aria-hidden="true" />
                            <div className="card-content">
                                <h3 className="card-title">Weapons</h3>
                                <p className="card-description">
                                    TONE's vertical spinner is a ferocious bite that sends opponents flying. Weapons team
                                    iterates to perfect this iconic attack system.
                                </p>
                                <button type="button" className="learn-more-btn dcrt-btn dcrt-btn--primary" onClick={() => navigate("/weapons")}>
                                    Learn more
                                </button>
                            </div>
                        </article>

                        <article className="card dcrt-panel" style={{ "--stagger": 3 }}>
                            <div className="card-corner" aria-hidden="true" />
                            <div className="card-icon card-icon--software" aria-hidden="true" />
                            <div className="card-content">
                                <h3 className="card-title">Software</h3>
                                <p className="card-description">
                                    The software team brings precision to every move—real-time commands and telemetry
                                    ensure full control and fast adaptation.
                                </p>
                                <button type="button" className="learn-more-btn dcrt-btn dcrt-btn--primary" onClick={() => navigate("/software")}>
                                    Learn more
                                </button>
                            </div>
                        </article>
                    </div>
                </section>
            </div>
            <Footer />
        </div>
    );
}

export default HomePage;
